import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Button, ButtonGroup, Card, Col, InputGroup, Nav, Navbar, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AdvancedMarker, APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import Select from "react-select";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import Icon from "@mdi/react";
import { mdiAlphaHCircleOutline, mdiBookOutline, mdiBroom, mdiChevronLeft, mdiCircle, mdiFullscreen, mdiMenu, mdiViewDashboardOutline } from "@mdi/js";

import { SelectGroupStartStyle, SelectOptions } from "../../config/select";
import { DefaultPosition, EscalaComprometimento } from "../../config/defines";
import DashboardService from "../../services/DashboardService";
import ClientService from "../../services/ClientService";
import UnitService from "../../services/UnitService";
import { DashboardGetMapResponseEntity } from "../../entities/DashboardEntity";

import Layout from "../../components/Layout";
import QuestionnaireService from "../../services/QuestionnaireService";
import { escalaCalculo } from "../../config/utils";

export default function DashboardMapa() {
	const dashboardService = new DashboardService();
	const questionnaireService = new QuestionnaireService();
	const clientService = new ClientService();
	const unitService = new UnitService();

	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();

	const containerRef = useRef<HTMLDivElement>(null);

	const [formBounds, setFormBounds] = useState<MapBoundsFilter | undefined>(
		searchParams.get("north")
			? {
					center: {
						lat: Number(searchParams.get("lat")),
						lng: Number(searchParams.get("lng")),
					},
					zoom: Number(searchParams.get("zoom")),
					north: Number(searchParams.get("north")),
					east: Number(searchParams.get("east")),
					south: Number(searchParams.get("south")),
					west: Number(searchParams.get("west")),
			  }
			: undefined
	);

	const [filtroQuestionnaire, setFiltroQuestionnaire] = useState<{ label: string; value: number } | null>(
		searchParams.get("questionnaireLabel")
			? {
					label: searchParams.get("questionnaireLabel") ?? "",
					value: Number(searchParams.get("questionnaireValue")),
			  }
			: null
	);
	const [filtroClient, setFiltroClient] = useState<{ label: string; value: number } | null>(
		searchParams.get("clientLabel")
			? {
					label: searchParams.get("clientLabel") ?? "",
					value: Number(searchParams.get("clientValue")),
			  }
			: null
	);
	const [filtroUnit, setFiltroUnit] = useState<{ label: string; value: number } | null>(
		searchParams.get("unitLabel")
			? {
					label: searchParams.get("unitLabel") ?? "",
					value: Number(searchParams.get("unitValue")),
			  }
			: null
	);

	const [list, setList] = useState(searchParams.get("list") === "1");
	const [listData, setListData] = useState<DashboardGetMapResponseEntity>([]);
	const [fullScreen, setFullScreen] = useState(true);

	const questionnaireIdRef = useRef<any>(null);
	const clientIdRef = useRef<any>(null);
	const unitIdRef = useRef<any>(null);

	const documentFull = document as any;
	const documentFullSupport = documentFull.fullscreenEnabled || documentFull.webkitFullscreenEnabled || documentFull.msFullscreenEnabled;

	const queryQuestionnaires = useQuery(["avaliacoes"], () => fetchDataQuestionnaires(), { refetchOnWindowFocus: false });
	const queryClientes = useQuery(["clientes"], () => fetchDataClientes(), { refetchOnWindowFocus: false });
	const queryUnidades = useQuery(["unidades", filtroClient?.value], () => fetchDataUnidades(filtroClient?.value), { enabled: !!filtroClient?.value, refetchOnWindowFocus: false });
	const queryMapa = useQuery(
		["dashboard", "mapa", filtroQuestionnaire?.value, filtroClient?.value, filtroUnit?.value, formBounds],
		() => fetchData(filtroQuestionnaire?.value, filtroClient?.value, filtroUnit?.value),
		{
			enabled: !!formBounds,
			refetchOnWindowFocus: false,
			refetchInterval: 60000,
			keepPreviousData: true,
		}
	);

	useEffect(() => {
		function handleFullscreenChange() {
			if (documentFull.fullscreenElement || documentFull.webkitFullscreenElement || documentFull.mozFullScreenElement || documentFull.msFullscreenElement) {
				containerRef.current!.style.setProperty("position", "fixed", "important");
				setFullScreen(false);
			} else {
				containerRef.current!.style.setProperty("position", "relative");
				setFullScreen(true);
			}
		}

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
		document.addEventListener("mozfullscreenchange", handleFullscreenChange);
		document.addEventListener("MSFullscreenChange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
			document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
			document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
			document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
		};
	}, []);

	useEffect(() => {
		if (formBounds) {
			setSearchParams({
				questionnaireLabel: filtroQuestionnaire?.label ?? "",
				questionnaireValue: filtroQuestionnaire?.value.toString() ?? "",
				clientLabel: filtroClient?.label ?? "",
				clientValue: filtroClient?.value.toString() ?? "",
				unitLabel: filtroUnit?.label ?? "",
				unitValue: filtroUnit?.value.toString() ?? "",
				lat: formBounds.center.lat.toString(),
				lng: formBounds.center.lng.toString(),
				zoom: formBounds.zoom.toString(),
				north: formBounds.north.toString(),
				east: formBounds.east.toString(),
				south: formBounds.south.toString(),
				west: formBounds.west.toString(),
				list: list ? "1" : "0",
			});
		}
	}, [filtroQuestionnaire, filtroClient, filtroUnit, formBounds, list]);

	async function fetchDataQuestionnaires() {
		const resp = await questionnaireService.getQuestionnaires();
		return resp.data;
	}

	async function fetchDataClientes() {
		let resp = await clientService.getClients();
		return resp.data;
	}

	async function fetchDataUnidades(clientId: any) {
		let resp = await unitService.getUnits(clientId);
		return resp.data;
	}

	async function fetchData(questionnaireId: any, clientId: any, unitId: any) {
		let resp = await dashboardService.getMap(questionnaireId, clientId, unitId, `${formBounds!.east}`, `${formBounds!.north}`, `${formBounds!.south}`, `${formBounds!.west}`);

		setListData(resp.data ?? []);

		if (resp.data && queryMapa.data) {
			if (
				(questionnaireIdRef.current !== null && questionnaireIdRef.current !== questionnaireId) ||
				(clientIdRef.current !== null && clientIdRef.current !== clientId) ||
				(unitIdRef.current !== null && unitIdRef.current !== unitId)
			) {
			} else {
				resp.data = [...queryMapa.data, ...resp.data].filter((item, index, self) => index === self.findIndex((other) => other.id === item.id));
			}
		}

		questionnaireIdRef.current = questionnaireId;
		clientIdRef.current = clientId;
		unitIdRef.current = unitId;

		return resp.data;
	}

	function handleFullScreen() {
		if (documentFullSupport) {
			if (documentFull.fullscreenElement) {
				documentFull.exitFullscreen();
			} else if (documentFull.webkitFullscreenElement) {
				documentFull.webkitExitFullscreen();
			} else if (documentFull.msFullscreenElement) {
				documentFull.msExitFullscreen();
			} else {
				documentFull.body.requestFullscreen();
			}
		}
	}

	function handleList() {
		setList(!list);
	}

	async function handleClick(id: number) {
		navigate("/avaliacaoDetalhes/" + id);
	}

	function handleLimpar() {
		setFiltroQuestionnaire(null);
		setFiltroClient(null);
		setFiltroUnit(null);
	}

	const formQuestionnaireOptions = queryQuestionnaires.data?.map((item) => {
		return { label: `${item.title} #${item.id}`, value: item.id };
	});

	const formClienteOptions = queryClientes.data?.map((item) => {
		return { label: `${item.name}`, value: item.id };
	});

	const formUnidadeOptions = queryUnidades.data?.map((item) => {
		return { label: `${item.name}`, value: item.id };
	});

	return (
		<Layout>
			<Navbar expand="lg" className="p-0 m-0">
				<h5 className="mt-4 mb-4 d-flex flex-fill flex-wrap gap-4 align-items-center fw-light">
					<Link to={"/dashboard"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiViewDashboardOutline} size={1} className="me-1" /> Dashboard Mapa
					</Link>
					{queryMapa.isRefetching && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex ms-auto fs-6" style={{ marginTop: fullScreen ? -10 : -25, marginBottom: -10, marginRight: fullScreen ? 0 : 90, zIndex: 1040 }}>
						<Navbar.Toggle aria-controls="filtros" />
					</div>
					<Navbar.Collapse id="filtros">
						<Nav className="d-flex ms-auto flex-wrap gap-2 fs-6" style={{ marginTop: fullScreen ? -10 : -100, marginBottom: -10, marginRight: fullScreen ? 0 : 90, zIndex: 1040 }}>
							<InputGroup className="shadow-sm" style={{ width: "auto" }}>
								<InputGroup.Text className="bg-white">Avaliação</InputGroup.Text>
								<Select
									placeholder={"Selecione"}
									value={filtroQuestionnaire}
									options={formQuestionnaireOptions}
									onChange={(value) => {
										setFiltroQuestionnaire(value);
									}}
									isClearable={true}
									styles={{
										...SelectGroupStartStyle,
										container: (styles: any, state: any) => {
											return {
												...styles,
												flex: 1,
												display: "flex",
												width: 150,
											};
										},
									}}
									{...SelectOptions}
								/>
							</InputGroup>
							<InputGroup className="shadow-sm" style={{ width: "auto" }}>
								<InputGroup.Text className="bg-white">Cliente</InputGroup.Text>
								<Select
									placeholder={"Selecione"}
									value={filtroClient}
									options={formClienteOptions}
									onChange={(value) => {
										setFiltroClient(value);
										if (!value) {
											setFiltroUnit(null);
										}
									}}
									isClearable={true}
									styles={{
										...SelectGroupStartStyle,
										container: (styles: any, state: any) => {
											return {
												...styles,
												flex: 1,
												display: "flex",
												width: 150,
											};
										},
									}}
									{...SelectOptions}
								/>
							</InputGroup>
							<InputGroup className="shadow-sm" style={{ width: "auto" }}>
								<InputGroup.Text className="bg-white">Unidade</InputGroup.Text>
								<Select
									placeholder={"Selecione"}
									value={filtroUnit}
									options={formUnidadeOptions}
									onChange={(value) => {
										setFiltroUnit(value);
									}}
									isClearable={true}
									styles={{
										...SelectGroupStartStyle,
										container: (styles: any, state: any) => {
											return {
												...styles,
												flex: 1,
												display: "flex",
												width: 150,
											};
										},
									}}
									{...SelectOptions}
								/>
							</InputGroup>
							<Button className="shadow-sm rounded-3" variant="light" onClick={handleLimpar}>
								<Icon path={mdiBroom} size={1} />
							</Button>
						</Nav>
					</Navbar.Collapse>
				</h5>
			</Navbar>

			<Card className="flex-fill border-0 shadow overflow-hidden" style={{ width: "100%", minHeight: 300 }}>
				<Card.Body className="d-flex p-0" style={{ height: 0 }}>
					<div className={`d-flex position-relative`} style={{ width: "100%", zIndex: 1030, top: 0, left: 0 }} ref={containerRef}>
						<APIProvider apiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}>
							{queryClientes.isFetching && <Spinner className="m-auto" />}
							{!queryClientes.isFetching && (
								<Map
									mapId={"dashboard"}
									defaultCenter={formBounds?.center ?? DefaultPosition}
									defaultZoom={formBounds?.zoom ?? 10}
									fullscreenControl={false}
									streetViewControl={false}
									zoomControlOptions={{ position: google.maps.ControlPosition?.BOTTOM_LEFT ?? 7 }}
									mapTypeControlOptions={{ position: google.maps.ControlPosition?.BOTTOM_CENTER ?? 6, mapTypeIds: ["roadmap", "satellite"] }}
									gestureHandling={"greedy"}
									style={{ width: "100%", height: "100%" }}
								>
									<DashboardMapaMarkers
										markers={queryMapa.data || []}
										onBoundsChange={(bounds) => {
											setFormBounds(bounds);
										}}
										onClusterClick={() => {
											setList(true);
										}}
									/>
								</Map>
							)}
						</APIProvider>
						<ButtonGroup className="shadow-sm" style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
							<Button variant="light" onClick={handleFullScreen} size="sm">
								<Icon path={mdiFullscreen} size={1} />
							</Button>
							<Button variant="light" onClick={handleList} size="sm">
								<Icon path={mdiMenu} size={1} />
							</Button>
						</ButtonGroup>
						{list && (
							<div
								className="d-flex flex-column bg-white rounded shadow-sm"
								style={{ position: "absolute", top: 54, right: 10, bottom: 20, zIndex: 2, width: 300, overflowY: "auto", overflowX: "hidden" }}
							>
								<h5 className="p-2 pb-0 d-flex align-items-center fw-light flex-wrap">
									<Icon path={mdiBookOutline} size={1} className="me-1" />
									Avaliações {queryMapa.isFetching && <Spinner size="sm" className="ms-1" variant="secondary" />}
								</h5>
								{listData.map((item, index) => {
									let escala = escalaCalculo(EscalaComprometimento, item.compromiseIndex * 100);
									return (
										<Row
											key={index}
											className="gap-1 border-bottom px-2 py-1"
											onClick={() => {
												handleClick(item.id);
											}}
											style={{ cursor: "pointer" }}
										>
											<Col>
												<div className={`text-nowrap text-muted fw-lighter small`}>Identificação</div>
												<div className={`fw-light small line-1`} title={`${item.id}`}>
													#{item.id ?? "..."}
												</div>
											</Col>
											{item.compromiseIndex !== undefined && (
												<Col className="d-flex align-items-center justify-content-end">
													<div
														className="d-flex align-items-center justify-content-center text-white rounded-circle shadow-sm fw-bold"
														style={{
															width: 30,
															height: 30,
															background: escala.data.cor,
															textShadow: "0 1px 2px black, 0 -1px 2px black",
															border: "2px dashed black",
															fontSize: 11,
														}}
													>
														{Math.round(item.compromiseIndex * 100)}
													</div>
												</Col>
											)}
											<Col lg={12}>
												<div className={`text-nowrap text-muted fw-lighter small`}>Endereço</div>
												<div className={`fw-light small`} title={`${item.endereco}`}>
													{item.endereco ?? "..."}
												</div>
											</Col>
										</Row>
									);
								})}
							</div>
						)}
					</div>
				</Card.Body>
			</Card>
		</Layout>
	);
}

type MapBoundsFilter = {
	center: { lat: number; lng: number };
	zoom: number;
	north: number;
	east: number;
	south: number;
	west: number;
};

type DashboardMapaMarkersProps = {
	base?: { lat: number; lng: number };
	sos?: { lat: number; lng: number };
	markers: DashboardGetMapResponseEntity;
	onBoundsChange: (bounds: MapBoundsFilter) => void;
	onClusterClick: () => void;
};

function DashboardMapaMarkers(props: DashboardMapaMarkersProps) {
	const map = useMap();
	const mapCluster = useMemo(() => {
		let markerCluster = new MarkerClusterer({
			map: map,
			// algorithmOptions: { maxZoom: 30 },
			onClusterClick: (event, cluster, map) => {
				map.fitBounds(cluster.bounds!);
				props.onClusterClick();
			},
		});

		return markerCluster;
	}, []);
	const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);

	useEffect(() => {
		if (map && mapCluster && markers) {
			mapCluster.clearMarkers();
			mapCluster.addMarkers(Object.values(markers));
		}
	}, [map, mapCluster, markers]);

	useEffect(() => {
		let markersArray = [...markers];
		for (let i = markersArray.length - 1; i >= 0; i--) {
			const marker = markersArray[i];
			const markerTitle = marker.getAttribute("title");

			const exists = props.markers.some((item) => item.id === Number(markerTitle));

			if (!exists) {
				markersArray.splice(i, 1);
			}
		}

		setMarkers(markersArray);
	}, [props.markers]);

	useEffect(() => {
		let onBoundsChangeTimeout: NodeJS.Timeout;

		function boundsChanged() {
			if (map) {
				const newBounds = map.getBounds()!;
				const center = map.getCenter();
				if (newBounds && center) {
					const boundsFilter = {
						center: { lat: center!.lat(), lng: center!.lng() },
						zoom: map.getZoom()!,
						north: newBounds.getNorthEast().lat(),
						east: newBounds.getNorthEast().lng(),
						south: newBounds.getSouthWest().lat(),
						west: newBounds.getSouthWest().lng(),
					};

					clearTimeout(onBoundsChangeTimeout);
					onBoundsChangeTimeout = setTimeout(() => {
						props.onBoundsChange(boundsFilter);
					}, 250);
				}
			}
		}

		if (map) {
			map.addListener("bounds_changed", boundsChanged);

			boundsChanged();

			return () => {
				clearTimeout(onBoundsChangeTimeout);
				google.maps.event.clearListeners(map, "bounds_changed");
			};
		}
	}, [map]);

	return (
		<>
			{props.base && (
				<DashboardMapaMarker id={-1} position={{ lat: props.base.lat, lng: props.base.lng }}>
					<Icon path={mdiAlphaHCircleOutline} size={1.5} className="text-danger bg-white rounded-circle" style={{ filter: "drop-shadow( 0 0 2px rgba(0, 0, 0, 0.5))" }} />
				</DashboardMapaMarker>
			)}
			{props.sos && (
				<DashboardMapaMarker id={-2} position={{ lat: props.sos.lat, lng: props.sos.lng }}>
					<Icon path={mdiAlphaHCircleOutline} size={1.5} className="text-air-blue bg-white rounded-circle" style={{ filter: "drop-shadow( 0 0 2px rgba(0, 0, 0, 0.5))" }} />
				</DashboardMapaMarker>
			)}
			{props.markers.map((marker) => {
				let escala = escalaCalculo(EscalaComprometimento, marker.compromiseIndex * 100);
				return (
					<DashboardMapaMarker
						key={marker.id}
						onRef={(item) => {
							setMarkers((prev) => [...prev, item]);
						}}
						id={marker.id}
						position={{ lat: marker.latitude, lng: marker.longitude }}
					>
						<div
							className="d-flex align-items-center justify-content-center text-white rounded-circle shadow-sm fw-bold"
							style={{
								width: 30,
								height: 30,
								background: marker.compromiseIndex !== undefined ? escala.data.cor : "#03c136",
								textShadow: "0 1px 2px black, 0 -1px 2px black",
								border: "2px dashed black",
							}}
						>
							{marker.compromiseIndex !== undefined ? Math.round(marker.compromiseIndex * 100) : "1"}
						</div>
					</DashboardMapaMarker>
				);
			})}
		</>
	);
}

type DashboardMapaMarkerProps = {
	onRef?: (ref: google.maps.marker.AdvancedMarkerElement) => void;
	id: number;
	position: { lat: number; lng: number };
	children: ReactNode | ReactNode[];
};

function DashboardMapaMarker(props: DashboardMapaMarkerProps) {
	const navigate = useNavigate();

	const markerRef = useCallback((marker: google.maps.marker.AdvancedMarkerElement) => {
		if (marker && props.onRef) {
			props.onRef(marker);
		}
		return marker;
	}, []);

	return (
		<AdvancedMarker
			title={`${props.id}`}
			position={props.position}
			ref={markerRef}
			onClick={() => {
				navigate("/avaliacaoDetalhes/" + props.id);
			}}
		>
			{props.children}
		</AdvancedMarker>
	);
}
